import { Document, Packer, Paragraph, TextRun, HeadingLevel, AlignmentType, BorderStyle, TabStopType, TabStopPosition } from "docx";
import { saveAs } from "file-saver";
import { profileData, experienceData, educationData, skillsData, toolsData, achievementsData } from "../data/mock";

const FONT = "Calibri";
const FONT_SIZE_NAME = 28;
const FONT_SIZE_HEADING = 22;
const FONT_SIZE_BODY = 20;
const FONT_SIZE_SMALL = 18;

const sectionHeading = (text) =>
  new Paragraph({
    children: [
      new TextRun({ text: text.toUpperCase(), bold: true, font: FONT, size: FONT_SIZE_HEADING, color: "000000" }),
    ],
    spacing: { before: 240, after: 80 },
    border: {
      bottom: { style: BorderStyle.SINGLE, size: 1, color: "999999", space: 4 },
    },
  });

const bulletPoint = (text) =>
  new Paragraph({
    children: [
      new TextRun({ text, font: FONT, size: FONT_SIZE_BODY }),
    ],
    bullet: { level: 0 },
    spacing: { after: 40 },
  });

const emptyLine = () =>
  new Paragraph({ children: [], spacing: { after: 60 } });

export const generateResumeDocx = async () => {
  const sections = [];

  // ── Header: Name ──
  sections.push(
    new Paragraph({
      children: [
        new TextRun({ text: profileData.name.toUpperCase(), bold: true, font: FONT, size: FONT_SIZE_NAME }),
      ],
      alignment: AlignmentType.LEFT,
      spacing: { after: 40 },
    })
  );

  // ── Contact line ──
  sections.push(
    new Paragraph({
      children: [
        new TextRun({ text: `${profileData.location} | ${profileData.phone} | ${profileData.email} | linkedin.com/in/lpace1`, font: FONT, size: FONT_SIZE_SMALL, color: "444444" }),
      ],
      spacing: { after: 40 },
    })
  );

  // ── Availability ──
  if (profileData.availability) {
    sections.push(
      new Paragraph({
        children: [
          new TextRun({ text: profileData.availability, font: FONT, size: FONT_SIZE_SMALL, italics: true, color: "555555" }),
        ],
        spacing: { after: 120 },
      })
    );
  }

  // ── Professional Summary ──
  sections.push(sectionHeading("Professional Summary"));
  sections.push(
    new Paragraph({
      children: [
        new TextRun({ text: profileData.summary, font: FONT, size: FONT_SIZE_BODY }),
      ],
      spacing: { after: 120 },
    })
  );

  // ── Core Competencies ──
  sections.push(sectionHeading("Core Competencies"));
  skillsData.forEach((skill) => {
    sections.push(
      new Paragraph({
        children: [
          new TextRun({ text: `${skill.category}: `, bold: true, font: FONT, size: FONT_SIZE_BODY }),
          new TextRun({ text: skill.description, font: FONT, size: FONT_SIZE_BODY }),
        ],
        spacing: { after: 40 },
      })
    );
  });
  sections.push(emptyLine());

  // ── Tools & Systems ──
  sections.push(sectionHeading("Tools & Systems"));
  sections.push(
    new Paragraph({
      children: [
        new TextRun({ text: toolsData.join(" | "), font: FONT, size: FONT_SIZE_BODY }),
      ],
      spacing: { after: 120 },
    })
  );

  // ── Professional Experience ──
  sections.push(sectionHeading("Professional Experience"));

  experienceData.forEach((role) => {
    // Job title + date on same line using tab
    sections.push(
      new Paragraph({
        children: [
          new TextRun({ text: role.role, bold: true, font: FONT, size: FONT_SIZE_BODY }),
          new TextRun({ text: "\t", font: FONT, size: FONT_SIZE_BODY }),
          new TextRun({ text: role.period, font: FONT, size: FONT_SIZE_BODY, color: "444444" }),
        ],
        tabStops: [{ type: TabStopType.RIGHT, position: TabStopPosition.MAX }],
        spacing: { before: 160, after: 20 },
      })
    );

    // Company + location
    sections.push(
      new Paragraph({
        children: [
          new TextRun({ text: `${role.company}`, font: FONT, size: FONT_SIZE_BODY, italics: true }),
          new TextRun({ text: role.companyLocation ? ` | ${role.companyLocation}` : "", font: FONT, size: FONT_SIZE_BODY, italics: true, color: "555555" }),
        ],
        spacing: { after: 60 },
      })
    );

    // Description
    if (role.description) {
      sections.push(
        new Paragraph({
          children: [
            new TextRun({ text: role.description, font: FONT, size: FONT_SIZE_BODY, color: "333333" }),
          ],
          spacing: { after: 60 },
        })
      );
    }

    // Bullet points
    role.highlights.forEach((point) => {
      sections.push(bulletPoint(point));
    });

    // Projects as sub-bullets
    if (role.projects && role.projects.length > 0) {
      sections.push(
        new Paragraph({
          children: [
            new TextRun({ text: "Key Projects:", bold: true, italics: true, font: FONT, size: FONT_SIZE_BODY }),
          ],
          spacing: { before: 60, after: 40 },
          indent: { left: 360 },
        })
      );
      role.projects.forEach((project) => {
        sections.push(
          new Paragraph({
            children: [
              new TextRun({ text: `${project.name}`, bold: true, font: FONT, size: FONT_SIZE_BODY }),
              new TextRun({ text: ` \u2014 ${project.description}`, font: FONT, size: FONT_SIZE_BODY }),
            ],
            bullet: { level: 1 },
            spacing: { after: 30 },
          })
        );
      });
    }
  });

  sections.push(emptyLine());

  // ── Selected Achievements ──
  sections.push(sectionHeading("Selected Achievements"));
  achievementsData.forEach((achievement) => {
    sections.push(bulletPoint(achievement));
  });

  sections.push(emptyLine());

  // ── Education ──
  sections.push(sectionHeading("Education"));
  educationData.forEach((edu) => {
    sections.push(
      new Paragraph({
        children: [
          new TextRun({ text: edu.degree, bold: true, font: FONT, size: FONT_SIZE_BODY }),
          new TextRun({ text: `, ${edu.field}`, font: FONT, size: FONT_SIZE_BODY }),
          new TextRun({ text: "\t", font: FONT, size: FONT_SIZE_BODY }),
          new TextRun({ text: edu.year, font: FONT, size: FONT_SIZE_BODY, color: "444444" }),
        ],
        tabStops: [{ type: TabStopType.RIGHT, position: TabStopPosition.MAX }],
        spacing: { after: 20 },
      })
    );
    sections.push(
      new Paragraph({
        children: [
          new TextRun({ text: edu.school, font: FONT, size: FONT_SIZE_BODY, italics: true, color: "555555" }),
        ],
        spacing: { after: 80 },
      })
    );
  });

  // ── Build & Download ──
  const doc = new Document({
    sections: [
      {
        properties: {
          page: {
            margin: { top: 720, bottom: 720, left: 720, right: 720 },
          },
        },
        children: sections,
      },
    ],
  });

  const blob = await Packer.toBlob(doc);
  saveAs(blob, "Layton_Pace_Resume.docx");
};
